import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';

import { MarvDateService } from '@marv/services';
import { ApiService } from '@app/services';
import { 
    Order, CustomerFeedBackValue, CustomerFeedBackCommentValue, FieldTeamStoreUniverse, 
    OrderRequest, MissionPlanningComplianceReport, MissionOrderCreateStatus, AdditionalTimes, Pcheck, MissionStatus,
    OperationCustomerPlanningSetting, PlanningSummary, PlanningSummaryRetailer, PlanningSummaryArea, PlanningSummarySector, 
    PlanningSummaryNbMission, MissionsPlanning, MissionPlanningFilters, WorkersSuggestionReport
} from '@app/models';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

    constructor(
        private apiService: ApiService
    ) {}

    async loadPlanning(filters: MissionPlanningFilters): Promise<MissionsPlanning|null> {
        let missionsPlanning: MissionsPlanning|null = null;   
        let response = await this.apiService.post('planning', filters);
        if(response?.data){
            missionsPlanning = MissionsPlanning.FromJson(response.data);
        }
        return missionsPlanning;
    }

    async loadToEvaluate(startDate: Date, endDate: Date, operationId?: number): Promise<MissionsPlanning|null> {
        let missionsPlanning: MissionsPlanning|null = null;  
        let from = MarvDateService.FromDate(startDate);    
        let to = MarvDateService.FromDate(endDate); 
        if(from && to){
            let body: MissionPlanningFilters = {
                displayOnlyUnratedMissions: true,
                from: from,
                to: to
            }; 
            if(operationId){
                body.operations = [operationId];
            }
            let response = await this.apiService.post('planning', body);
            if(response?.data){
                missionsPlanning = MissionsPlanning.FromJson(response.data);
            }
        }
        return missionsPlanning;
    }
    
    async searchOrders(startDate: Date, endDate: Date, storeId?: number, operationId?: number): Promise<Order[]>{
        let orders: Array<Order> = [];
        let params: any = {
            from: MarvDateService.FromDate(startDate),
            to: MarvDateService.FromDate(endDate)
        };
        if(storeId){
            params.store = storeId;
        }
        if(operationId){
            params.operation = operationId;
        }
        let response = await this.apiService.get('order/search', params);
        if(response?.data){
            for(let d of response.data){
                orders.push(Order.FromJson(d));
            }
        }
        return orders;
    }
    
    async loadOrder(idOrder: number): Promise<Order|null>{
        let order: Order|null = null;
        let response = await this.apiService.get('order/'+idOrder);
        if(response?.data){
            order = Order.FromJson(response.data);
        }
        return order;
    }
            
    async updateCustomerFeedBack(idOrder: number, customerFeedback: string): Promise<boolean> {
        let res: boolean = false;
        let params = {
            cf: customerFeedback
        };
        let response = await this.apiService.put('order/' + idOrder + '/customer-feedback', null, params);
        if(response?.data){
            res = true;
        } 
        return res;
    }

    async updateCustomerFeedBackComment(idOrder: number, customerFeedbackComment: number): Promise<boolean> {
        let res: boolean = false;
        let response = await this.apiService.put('order/' + idOrder + '/customer-feedback-comment', customerFeedbackComment);
        if(response?.data){
            res = true;
        } 
        return res;
    }
    
    async loadCurrentFieldTeam(skipStore: boolean, storeId?: number): Promise<FieldTeamStoreUniverse|null> {        
        let fieldTeamStoreUniverse: FieldTeamStoreUniverse|null = null;
        let params: any = {
            skipStore: skipStore
        };      
        if(storeId){
            params.storeId = storeId;
        }
        let response = await this.apiService.get('field-team/current', params);
        if(response?.data){
            fieldTeamStoreUniverse = FieldTeamStoreUniverse.FromApi(response.data)
        } 
        return fieldTeamStoreUniverse;
    }
    
    async checkPlanningCompliance(orderRequest: OrderRequest): Promise<MissionPlanningComplianceReport|null> {
        let missionPlanningComplianceReport: MissionPlanningComplianceReport|null = null;
        let body: any = Object.assign({}, orderRequest);
        delete body.profilName;
        let response = await this.apiService.post('order/operation/' + orderRequest.operation + '/customer-planning-setting-compliance', body);
        if(response?.data){
            missionPlanningComplianceReport = response.data;
        }
        return missionPlanningComplianceReport;
    }
    
    async createOrder(orderRequest: OrderRequest): Promise<MissionOrderCreateStatus|null> {
        let createStatus : MissionOrderCreateStatus|null = null;
        let body: any = Object.assign({}, orderRequest);
        delete body.profilName;
        let response = await this.apiService.post('order/', body);
        if(response?.data){
            createStatus = response?.data;
        }
        return createStatus;
    }
    
    async updateOrder(orderId: number, orderRequest: OrderRequest): Promise<MissionOrderCreateStatus|null> {
        let createStatus : MissionOrderCreateStatus|null = null;
        let body: any = Object.assign({}, orderRequest);
        delete body.profilName;
        let response = await this.apiService.put('order/' + orderId, body);
        if(response?.data){
            createStatus = response?.data;
        }
        return createStatus;
    }
    
    async deleteOrder(orderId: number): Promise<boolean> {
        let deleteStatus: boolean = false;
        let response = await this.apiService.delete('order/' + orderId);
        if(response?.data){
            deleteStatus = response?.data;
        }
        return deleteStatus;
    }
    
    async deleteActivity(activityId: number): Promise<boolean> {
        let deleteStatus: boolean = false;
        let response = await this.apiService.delete('operational/activity/' + activityId);
        if(response.error === null){
            deleteStatus = true;
        }
        return deleteStatus;
    }
    
    async cancelOrder(orderId: number, comments: string): Promise<boolean> {
        let cancelStatus: boolean = false;
        let body: any = {
            comments: comments
        }
        let response = await this.apiService.post('order/' + orderId + '/customer-cancel-request', body);
        if(response?.data){
            cancelStatus = response?.data;
        }
        return cancelStatus
    }
    
    async uploadClientBriefMission(orderId: number, content: ArrayBuffer, metadata: {type: string, name: string}): Promise<any> {
        
        let formData = new FormData();
        formData.append("filename", metadata.name)
        formData.append("id", orderId.toString());
        formData.append("mimeType", metadata.type);
        formData.append("file", new Blob([content]));
        
        let response = await this.apiService.put('order/' + orderId + '/client-brief-mission', formData);
        return (response.error === null);
    }
    
    
    async loadClientBriefMission(orderId: number): Promise<Blob|null> {   
        let pdfContent: Blob|null = null;     
        let response = await this.apiService.get('order/' + orderId + '/dl/client-brief-mission', null, true, new HttpHeaders(), 'blob');
        if(response?.data){
            pdfContent = response.data;
        }
        return pdfContent;
    }
      
    async loadAdditionalWorkingTime(orderId: number): Promise<AdditionalTimes[]> {
        let additionalTimes: Array<AdditionalTimes> = [];        
        let response = await this.apiService.get('order/' + orderId + '/additional-times');
        if(response?.data){
            additionalTimes = response.data;
        } 
        return additionalTimes;
    }
    
    async saveAdditionalWorkingTime(orderId: number, additionalTimes: AdditionalTimes[]): Promise<any> {
        let response = await this.apiService.put('order/' + orderId + '/additional-times', additionalTimes);
        return response?.data;
    }
    
    async loadPcheck(orderId: number): Promise<Pcheck[]> {
        let tabPcheck: Array<Pcheck> = [];
        let response = await this.apiService.get('operational/presencecheck/mission/' + orderId);
        if(response?.data){
            tabPcheck = response.data;
        } 
        return tabPcheck;
    }
    
    async loadPlanningSummary(operationsSettings: Map<number, OperationCustomerPlanningSetting>, operationId: number, operationSource: string, startDate: Date, endDate: Date): Promise<PlanningSummary|null>{
        let planningSummary: PlanningSummary|null = null;      
        
        let from = MarvDateService.FromDate(startDate);    
        let to = MarvDateService.FromDate(endDate); 
        if(from && to){
            let filters: MissionPlanningFilters = {
                from: from,
                to: to
            };

            let report = await this.loadPlanning(filters);
            if(report){

                let today = MarvDateService.GetToday();
                let retailersMap: Map<string, PlanningSummaryRetailer> = new Map();
                let areasMap: Map<number, any> = new Map();
                let nbMissions: Map<string, PlanningSummaryNbMission> = new Map();

                report.getMissionsItems(operationsSettings, true).forEach(m => {
                    if(m.operation.source == operationSource && m.operation.id == operationId
                        && [MissionStatus.Status.NONE, MissionStatus.Status.CANCELED, MissionStatus.Status.REJECTED].indexOf(m.status.code) === -1){

                        if(!retailersMap.has(m.retailer.code)){
                            retailersMap.set(m.retailer.code, new PlanningSummaryRetailer(m.retailer.code, m.retailer.name));                        
                        }

                        let area = areasMap.get(m.customerArea.id);
                        if(area === undefined){
                            area = {
                                name: m.customerArea.name,
                                nbMissions: new Map<string, PlanningSummaryNbMission>(),
                                sectorsMap: new Map<number, any>()
                            };
                        }

                        let sector = area.sectorsMap.get(m.customerSector.id);
                        if(sector === undefined){
                            sector = {
                                name: m.customerSector.name,
                                nbMissions: new Map<string, PlanningSummaryNbMission>()
                            };
                        }

                        let nbMissionsSector = sector.nbMissions.get(m.retailer.code) || new PlanningSummaryNbMission();
                        nbMissionsSector.all++;
                        if(m.day < today){
                            nbMissionsSector.done++;
                        }
                        else{
                            nbMissionsSector.planned++;
                        }
                        sector.nbMissions.set(m.retailer.code, nbMissionsSector);

                        let nbMissionsArea = area.nbMissions.get(m.retailer.code) || new PlanningSummaryNbMission();
                        nbMissionsArea.all++;
                        if(m.day < today){
                            nbMissionsArea.done++;
                        }
                        else{
                            nbMissionsArea.planned++;
                        }
                        area.nbMissions.set(m.retailer.code, nbMissionsArea);

                        let nbMissionsTotal = nbMissions.get(m.retailer.code) || new PlanningSummaryNbMission();
                        nbMissionsTotal.all++;
                        if(m.day < today){
                            nbMissionsTotal.done++;
                        }
                        else{
                            nbMissionsTotal.planned++;
                        }
                        nbMissions.set(m.retailer.code, nbMissionsTotal);

                        area.sectorsMap.set(m.customerSector.id, sector);
                        areasMap.set(m.customerArea.id, area);
                    }
                });

                if(retailersMap.size > 0 && areasMap.size > 0){

                    let retailers: Array<PlanningSummaryRetailer> = [];
                    retailersMap.forEach(r => {
                        retailers.push(r);
                    });
                    retailers.sort((a, b) => {
                        if ( a.name < b.name ){
                            return -1;
                        }
                        if ( a.name > b.name ){
                            return 1;
                        }
                        return 0;
                    });

                    let areas: Array<PlanningSummaryArea> = [];
                    areasMap.forEach( (a: any) => {

                        let area = new PlanningSummaryArea(a.name);
                        area.nbMissions = a.nbMissions;

                        a.sectorsMap.forEach((s: any) => {                        
                            let sector = new PlanningSummarySector(s.name);
                            sector.nbMissions = s.nbMissions;
                            area.sectors.push(sector);                        
                        });

                        area.sectors.sort((a, b) => {
                            if ( a.name < b.name ){
                                return -1;
                            }
                            if ( a.name > b.name ){
                                return 1;
                            }
                            return 0;
                        });

                        areas.push(area);
                    });

                    areas.sort((a, b) => {
                        if ( a.name < b.name ){
                            return -1;
                        }
                        if ( a.name > b.name ){
                            return 1;
                        }
                        return 0;
                    });

                    planningSummary = new PlanningSummary();
                    planningSummary.retailers = retailers;
                    planningSummary.areas = areas;
                    planningSummary.nbMissions = nbMissions;

                }
            }    
        }    
        return planningSummary;
    }    
    
    async loadAdditionalsDatas(missionId: number): Promise<any>{
        let data: any = null;
        let response = await this.apiService.get('order/'+missionId+'/customer-directions-questionnaire');
        if(response?.data?.directionsPayload){
            data = JSON.parse(response.data.directionsPayload);
        }
        return data;
    }
    
    async saveAdditionalsDatas(missionId: number, data: any): Promise<boolean>{       
        let body: any = {
            payload: JSON.stringify(data)
        };
        let response = await this.apiService.post('order/'+missionId+'/customer-directions-questionnaire', body);
        return (response.error === null);
    }
    
    async loadWorkersSuggestionList(operationId: number, storeId: number, epochDays: Array<number>): Promise<WorkersSuggestionReport>{    
        let data: WorkersSuggestionReport = new WorkersSuggestionReport();   
        let body: any = {
            operationId: operationId,
            storeId: storeId,
            days: epochDays
        };
        let response = await this.apiService.post('order/candidates-report', body);
        if(response?.data){
            data = response.data;
        }
        return data;
    }
}

